/**
 * useFieldOptions — fetch and cache Airtable single-select options.
 *
 * Schema endpoint is hit once per (table, field) pair per session and cached
 * with a long staleTime — option lists rarely change and we want dropdowns
 * to render instantly. Falls back to a default option list when the schema
 * endpoint hasn't responded yet (or fails) so dropdowns are never empty.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { schemaApi } from '@/lib/api';

export interface FieldOption {
  name: string;
  color?: string;
}

const SESSION_STALE = 60 * 60 * 1000; // 1 hour — refetch on next session

export function useFieldOptions(
  field: string,
  table = 'ENT_Projects',
): UseQueryResult<{
  table: string;
  field: string;
  type: string;
  options: FieldOption[];
}> {
  return useQuery({
    queryKey: ['schema', 'field-options', table, field],
    queryFn: () => schemaApi.statusOptions(table, field),
    staleTime: SESSION_STALE,
    gcTime: SESSION_STALE,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
